import React, { useCallback, useMemo } from "react";
import { ProductProfile } from "../../../components/ProductProfile/ProductProfile";
import { Product, ProductFormFields } from "@design-controls/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useCurrentProduct } from "../../../hooks/useCurrentProduct";
import { useUpdateProduct } from "../../../hooks/product/useUpdateProduct";
import { useCreateResourceLibraryProduct } from "../../../hooks/resourceLibrary/useCreateResourceLibraryProduct";

type Props = {
  nextRef: React.RefObject<any>;
};

export const ProductConfigure: React.FC<Props> = ({ nextRef }) => {
  const product = useCurrentProduct();
  const { mutate } = useUpdateProduct();
  const createResourceLibraryProduct = useCreateResourceLibraryProduct();

  // `id` is deliberately absent: it is not an input, and the union's branches
  // are built from `ProductWithoutId`. It is added back when we build the
  // payload, from the product we already hold.
  const defaultValues = useMemo(() => {
    const { name, type, tags, productQri } = product;

    return {
      name,
      type,
      tags,
      ...(productQri
        ? { productSource: "existing" as const, productQri }
        : { productSource: "none" as const }),
    };
  }, [product]);

  const methods = useForm<ProductFormFields>({
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(ProductFormFields),
  });

  const onSubmit = useCallback(
    async (formValues: ProductFormFields) => {
      let productQri: string | null = null;

      if (formValues.productSource === "existing") {
        productQri = formValues.productQri;
      } else if (formValues.productSource === "new") {
        const { matrixQRI } = await createResourceLibraryProduct.mutateAsync(
          formValues.resourceLibraryProductToCreate,
        );
        productQri = matrixQRI;
        // Write the new product back into the form so that if the save fails,
        // a retry links the Resource Library product we just created rather
        // than creating a second one.
        methods.setValue("productSource", "existing");
        methods.setValue("productQri", matrixQRI);
      }

      // Parse to trim the form-only fields the API does not care about.
      mutate(Product.parse({ ...formValues, id: product.id, productQri }));
    },
    [createResourceLibraryProduct, methods, mutate, product.id],
  );

  return (
    <FormProvider {...methods}>
      <ProductProfile />
      <button
        style={{ display: "none" }}
        ref={nextRef}
        onClick={methods.handleSubmit(onSubmit)}
        type="submit"
      />
    </FormProvider>
  );
};
